import type { Edge, Node } from '@xyflow/react';
import type {
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';
import {
  ATTACK_PATH_EVENT_COLUMN_WIDTH,
  ATTACK_PATH_EVENT_ROW_HEIGHT,
} from './build-attack-path-event-graph.util';
import { CROWN_JEWEL_CLUSTER_NODE_ID } from './assessment-attack-graph-layout.constants';
import type { AssessmentAttackGraph } from './assessment-attack-graph.types';

const isCrownJewelSeedNode = (node: Node<AttackPathEventNodeData>): boolean =>
  node.data.role === 'CROWN_JEWEL_SEED';

const provenReachedJewelIds = ({
  nodes,
  edges,
}: {
  nodes: ReadonlyArray<Node<AttackPathEventNodeData>>;
  edges: ReadonlyArray<Edge<AttackPathEventEdgeData>>;
}): Set<string> => {
  const jewelIds = new Set(
    nodes.filter(isCrownJewelSeedNode).map((node) => node.id),
  );

  return new Set(
    edges
      .filter(
        (edge) => edge.data?.variant === 'proven' && jewelIds.has(edge.target),
      )
      .map((edge) => edge.target),
  );
};

const buildClusterNode = ({
  column,
  lane,
  hiddenCount,
  expanded,
  occurredAt,
}: {
  column: number;
  lane: number;
  hiddenCount: number;
  expanded: boolean;
  occurredAt: string;
}): Node<AttackPathEventNodeData> => ({
  id: CROWN_JEWEL_CLUSTER_NODE_ID,
  type: 'attackPathEventNode',
  position: {
    x: column * ATTACK_PATH_EVENT_COLUMN_WIDTH,
    y: lane * ATTACK_PATH_EVENT_ROW_HEIGHT,
  },
  data: {
    role: 'CROWN_JEWEL_CLUSTER',
    campaignId: null,
    campaignStatus: null,
    isCampaignRunning: false,
    chainProgress: 0,
    kind: 'HOST_DISCOVERED',
    typeLabel: 'Crown jewels',
    title: 'Crown jewels',
    subtitle: expanded ? null : String(hiddenCount),
    caption: expanded ? 'Show less' : 'Show more',
    weaknessId: null,
    mitreTechniqueId: null,
    provenance: null,
    privilegeTier: null,
    resourceKind: null,
    severity: null,
    status: 'SUCCEEDED',
    isProven: false,
    exploitsWeakness: false,
    isCrownJewel: true,
    isDomainController: false,
    impacts: [],
    elapsedSeconds: 0,
    occurredAt,
    hopIndex: hiddenCount,
    retryCount: 0,
    findingId: null,
    hostname: null,
    entityKind: null,
    entityId: null,
    objective: null,
    signalId: null,
    durationSeconds: null,
    chainStatus: 'COMPLETED',
    column,
    lane,
  },
});

const retargetCollapsedJewelEdges = ({
  edges,
  collapsedJewelIds,
}: {
  edges: ReadonlyArray<Edge<AttackPathEventEdgeData>>;
  collapsedJewelIds: ReadonlySet<string>;
}): Edge<AttackPathEventEdgeData>[] => {
  const seen = new Set<string>();
  const next: Edge<AttackPathEventEdgeData>[] = [];

  for (const edge of edges) {
    if (!collapsedJewelIds.has(edge.target)) {
      next.push(edge);
      continue;
    }

    const key = `${edge.source}->${CROWN_JEWEL_CLUSTER_NODE_ID}:${edge.data?.campaignId ?? ''}:${edge.data?.variant ?? ''}`;

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    next.push({
      ...edge,
      id: `${edge.id}:cluster`,
      target: CROWN_JEWEL_CLUSTER_NODE_ID,
    });
  }

  return next;
};

const layoutJewelColumn = ({
  nodes,
}: {
  nodes: Node<AttackPathEventNodeData>[];
}): Node<AttackPathEventNodeData>[] => {
  const jewelColumnNodes = nodes
    .filter(
      (node) =>
        node.data.role === 'CROWN_JEWEL_SEED' ||
        node.data.role === 'CROWN_JEWEL_CLUSTER',
    )
    .sort((left, right) => {
      if (left.data.role !== right.data.role) {
        return left.data.role === 'CROWN_JEWEL_CLUSTER' ? -1 : 1;
      }

      return left.position.y - right.position.y;
    });
  const column =
    jewelColumnNodes[0]?.data.column ??
    nodes.find(isCrownJewelSeedNode)?.data.column ??
    0;
  const laneById = new Map(
    jewelColumnNodes.map((node, index) => [node.id, index]),
  );

  return nodes.map((node) => {
    const lane = laneById.get(node.id);

    if (lane === undefined) {
      return node;
    }

    return {
      ...node,
      position: {
        x: column * ATTACK_PATH_EVENT_COLUMN_WIDTH,
        y: lane * ATTACK_PATH_EVENT_ROW_HEIGHT,
      },
      data: {
        ...node.data,
        column,
        lane,
      },
    };
  });
};

export const applyCrownJewelClusterView = ({
  graph,
  expanded,
}: {
  graph: AssessmentAttackGraph;
  expanded: boolean;
}): AssessmentAttackGraph => {
  const jewelNodes = graph.nodes.filter(isCrownJewelSeedNode);

  if (jewelNodes.length <= 1) {
    return graph;
  }

  const reachedIds = provenReachedJewelIds({
    nodes: graph.nodes,
    edges: graph.edges,
  });
  const collapsedJewelIds = expanded
    ? new Set<string>()
    : new Set(
        jewelNodes
          .filter((node) => !reachedIds.has(node.id))
          .map((node) => node.id),
      );

  if (!expanded && collapsedJewelIds.size === 0) {
    return graph;
  }

  const column = jewelNodes[0]?.data.column ?? 0;
  const occurredAt =
    jewelNodes[0]?.data.occurredAt ?? new Date(0).toISOString();
  const withoutCollapsed = graph.nodes.filter(
    (node) =>
      node.data.role !== 'CROWN_JEWEL_CLUSTER' &&
      !collapsedJewelIds.has(node.id),
  );
  const cluster = buildClusterNode({
    column,
    lane: 0,
    hiddenCount: expanded ? jewelNodes.length : collapsedJewelIds.size,
    expanded,
    occurredAt,
  });
  const nodes = layoutJewelColumn({
    nodes: [...withoutCollapsed, cluster],
  });
  const edges = expanded
    ? graph.edges.filter(
        (edge) =>
          edge.source !== CROWN_JEWEL_CLUSTER_NODE_ID &&
          edge.target !== CROWN_JEWEL_CLUSTER_NODE_ID,
      )
    : retargetCollapsedJewelEdges({
        edges: graph.edges,
        collapsedJewelIds,
      });

  return {
    ...graph,
    nodes,
    edges,
  };
};
